// filterView/variants/formView.js — variant C "Formular" of the documents.php filter views (plan documents_admin-search,
// phase filter-varianten): today's form, grouped and calmer. The schema groups are <details> sections ("N aktiv" in the
// summary), each field has its value control and a small mode menu; the same sentence with the all / at least one
// toggle as the builder.
//
// One condition per field (User 2026-10-13): a field shows the FIRST condition of the list with that field; more of them
// (built in B) → "+N weitere im Baukasten", C edits only the first. Typing into an empty field adds the condition,
// emptying a text field removes it, a list's "alle" removes it; changing only the mode of an existing condition keeps it
// (possibly unfinished — not sent). Without a condition a changed mode stays local until a value comes, except a mode
// without value, which is a condition by itself.
//
// The form is built once (fields are fixed by the schema) and only updated, so typing keeps focus.
//
// createFormView({ schema, store }) → { element, destroy }

import { createDisposers } from '../../core/disposers.js';
import { addCondition, updateCondition, removeCondition, validate } from '../conditionSet.js';
import { createValueInput } from '../parts/valueInput.js';
import { createCombineSentence } from '../parts/combineToggle.js';

const PROBLEM_TEXTS = { date: 'FILTER_VIEW_PROBLEM_DATE', option: 'FILTER_VIEW_PROBLEM_OPTION', id: 'FILTER_VIEW_PROBLEM_ID' };
let instances = 0;   // keeps label / control ids unique if a page ever holds two forms

function createFormField({ schema, store, field, idPrefix }) {
    const disposers = createDisposers();
    const texts = schema.texts;
    const controlId = `${idPrefix}-${field.key}`;
    const modeOf = (key) => field.modes.find((m) => m.key === key);

    let condition = null;               // the field's first condition, as last shown
    let pendingMode = field.modes[0].key;   // mode chosen while there is no condition
    let needsValue = null;
    let valueInput = null;
    disposers.add(() => valueInput?.destroy());

    const element = document.createElement('div');
    element.className = 'topo2-filterview-field';

    const label = document.createElement('label');
    label.className = 'topo2-filterview-field-label';
    label.htmlFor = controlId;
    const name = document.createElement('span');
    name.textContent = field.label;
    const flag = document.createElement('span');
    flag.className = 'topo2-filterview-flag';
    const more = document.createElement('span');
    more.className = 'topo2-filterview-more';
    label.append(name, flag, more);

    const controls = document.createElement('div');
    controls.className = 'topo2-filterview-field-controls';
    const valueCell = document.createElement('div');
    valueCell.className = 'topo2-filterview-value';
    controls.append(valueCell);

    let modeEl = null;
    if (field.modes.length > 1) {
        modeEl = document.createElement('select');
        modeEl.className = 'topo2-field--bordered topo2-filterview-select topo2-filterview-mode';
        modeEl.setAttribute('aria-label', `${texts.FILTER_VIEW_MODE}: ${field.label}`);
        modeEl.append(...field.modes.map((m) => new Option(m.label, m.key)));
        const onMode = () => {
            const mode = modeEl.value;
            if (condition) {
                store.set((set) => updateCondition(set, schema, condition.id, { mode }));
            } else if (!modeOf(mode).value) {
                store.set((set) => addCondition(set, schema, { field: field.key, mode }));
            } else {
                pendingMode = mode;
                show(null, 0, null);   // value control may change (e.g. from "ist leer" back to "enthält")
            }
        };
        modeEl.addEventListener('change', onMode);
        disposers.add(() => modeEl.removeEventListener('change', onMode));
        controls.append(modeEl);
    }
    element.append(label, controls);

    const onValue = (value) => {
        if (condition && value === '') {
            store.set((set) => removeCondition(set, condition.id));
        } else if (condition) {
            store.set((set) => updateCondition(set, schema, condition.id, { value }));
        } else if (value !== '') {
            store.set((set) => addCondition(set, schema, { field: field.key, mode: pendingMode, value }));
        }
    };

    function show(next, moreCount, problem) {
        condition = next;
        const mode = condition ? condition.mode : pendingMode;
        if (condition) {
            // once the condition is gone the field keeps its mode ("beginnt mit" stays after emptying) — but not a
            // mode without value, which without a condition would look like an active "ist leer"
            pendingMode = modeOf(condition.mode).value ? condition.mode : field.modes[0].key;
        }
        const modeNeedsValue = modeOf(mode).value;
        if (needsValue !== modeNeedsValue) {
            valueInput?.destroy();
            valueInput = createValueInput({
                field, needsValue: modeNeedsValue, value: condition?.value ?? '', onInput: onValue, id: controlId,
                emptyLabel: field.kind === 'list' ? texts.SEARCH_NOT : undefined,
            });
            valueCell.replaceChildren(valueInput.element);
            needsValue = modeNeedsValue;
        } else {
            valueInput.setValue(condition?.value ?? '');
        }
        if (modeEl) {
            modeEl.value = mode;
        }

        flag.textContent = condition && mode !== field.modes[0].key ? ` · ${modeOf(mode).label}` : '';
        more.textContent = moreCount > 0 ? ` ${texts.FILTER_VIEW_MORE.replace('{n}', moreCount)}` : '';
        element.classList.toggle('is-set', condition !== null);

        const message = PROBLEM_TEXTS[problem] ? texts[PROBLEM_TEXTS[problem]] : '';
        element.classList.toggle('is-invalid', message !== '');
        valueCell.title = message;
        if (modeNeedsValue && message) {
            valueInput.element.setAttribute('aria-invalid', 'true');
        } else {
            valueInput.element.removeAttribute('aria-invalid');
        }
    }

    return { element, update: show, destroy: () => disposers.dispose() };
}

export function createFormView({ schema, store }) {
    const disposers = createDisposers();
    const texts = schema.texts;
    const idPrefix = `topo2-fv-form${++instances}`;

    const element = document.createElement('div');
    element.className = 'topo2-filterview topo2-filterview--form';

    const lead = createCombineSentence({ store, texts });
    disposers.add(() => lead.destroy());
    element.append(lead.element);

    const groups = [];
    const fields = new Map();   // field key → form field
    for (const group of schema.groups) {
        const groupFields = schema.fields.filter((f) => f.group === group.key);
        if (!groupFields.length) {
            continue;
        }
        const details = document.createElement('details');
        details.className = 'topo2-filterview-group';
        const summary = document.createElement('summary');
        const title = document.createElement('span');
        title.textContent = group.label;
        const count = document.createElement('span');
        count.className = 'topo2-filterview-count';
        summary.append(title, count);
        const grid = document.createElement('div');
        grid.className = 'topo2-filterview-fields';
        for (const field of groupFields) {
            const formField = createFormField({ schema, store, field, idPrefix });
            disposers.add(() => formField.destroy());
            fields.set(field.key, formField);
            grid.append(formField.element);
        }
        details.append(summary, grid);
        element.append(details);
        groups.push({ group, details, count, keys: groupFields.map((f) => f.key), active: null });
    }

    const render = (set) => {
        lead.update(set.combine);
        const problems = new Map(validate(set, schema).map((p) => [p.id, p.reason]));
        for (const [key, formField] of fields) {
            const ofField = set.conditions.filter((c) => c.field === key);
            const first = ofField[0] ?? null;
            formField.update(first, Math.max(ofField.length - 1, 0), first ? problems.get(first.id) ?? null : null);
        }
        for (const entry of groups) {
            const active = entry.keys.filter((key) => set.conditions.some((c) => c.field === key)).length;
            entry.count.textContent = active ? texts.FILTER_VIEW_ACTIVE.replace('{n}', active) : '';
            // open at the start (content or with conditions) and when a group gets its first condition from elsewhere;
            // otherwise the user's open / closed stays
            if (entry.active === null) {
                entry.details.open = entry.group.key === 'content' || active > 0;
            } else if (entry.active === 0 && active > 0) {
                entry.details.open = true;
            }
            entry.active = active;
        }
    };

    disposers.add(store.subscribe(render));
    render(store.get());

    return {
        element,
        destroy() {
            disposers.dispose();
        },
    };
}
